import { useEffect, useState } from 'react';
import { STATES } from '~/constants';
import { getEmployerFacilities } from '~/features/market/facility/api';
import { subspecialtiesToOptions } from '~/features/market/listings/subspecialties';
import { searchGeographies } from '~/features/provider-hlx/service-listings/geographyApi';
import type { ProviderCategory } from '~/types';
import { API_CALLS, type CompareListingOption } from './api';
import type { OfferingSearchValues } from './offeringSearch';

// The buyer's own listings a Buying-tab fit can be compared against: what they are hiring for,
// and still open.
export const isComparable = (listing: CompareListingOption) =>
  listing.listingSide !== 'OFFERING' && listing.activeFlag === true && !listing.filledFlag;

export const useCompareListings = (employerId: number | undefined) => {
  const [state, setState] = useState<{
    employerId: number | undefined;
    listings: CompareListingOption[];
    loaded: boolean;
  }>({ employerId: undefined, listings: [], loaded: false });
  useEffect(() => {
    if (!employerId) return;
    let cancelled = false;
    API_CALLS.getCompareListings(employerId)
      .then((rows) => {
        if (!cancelled)
          setState({ employerId, listings: (rows ?? []).filter(isComparable), loaded: true });
      })
      .catch(() => {
        // Without the list the picker is simply empty; searching still works.
        if (!cancelled) setState({ employerId, listings: [], loaded: true });
      });
    return () => {
      cancelled = true;
    };
  }, [employerId]);
  const current = state.employerId === employerId ? state : undefined;
  return { listings: current?.listings ?? [], loaded: current?.loaded ?? false };
};

const zipOf = (postalCode: string | undefined) => postalCode?.slice(0, 5) || undefined;

// The facilities of a listing as the by-employer read serializes them: shallow employer-facility
// rows with an id but no `facility.address` (a listing-facility link carries `employerFacilityId`).
const facilityZip = async (listing: CompareListingOption): Promise<string | undefined> => {
  for (const facility of listing.facilities) {
    const zip = zipOf('facility' in facility ? facility.facility?.address?.postalCode : undefined);
    if (zip) return zip;
  }
  const ids = listing.facilities.map((facility) =>
    'employerFacilityId' in facility ? facility.employerFacilityId : facility.id
  );
  if (ids.length === 0) return undefined;
  try {
    // The employer's own facilities come with their addresses.
    const employerFacilities = await getEmployerFacilities();
    for (const id of ids) {
      const zip = zipOf(
        employerFacilities.find((employerFacility) => employerFacility.id === id)?.facility?.address
          ?.postalCode
      );
      if (zip) return zip;
    }
  } catch {
    // Without an address the location stays empty for the buyer to fill in.
  }
  return undefined;
};

/**
 * DD8: choosing "Compare to my listing" fills the filters from that listing. Every value stays
 * editable. The location is the listing's first facility with an address, looked up by ZIP; a
 * listing with no facility address leaves the location empty.
 */
export const prefillFromListing = async (
  listing: CompareListingOption,
  categories: ProviderCategory[] | undefined
): Promise<OfferingSearchValues> => {
  const zip = await facilityZip(listing);
  let location: OfferingSearchValues['location'];
  if (zip) {
    try {
      const [geography] = await searchGeographies(zip);
      if (geography)
        location = {
          value: String(geography.id),
          label: geography.label,
          latitude: Number(geography.latitude),
          longitude: Number(geography.longitude),
        };
    } catch {
      location = undefined;
    }
  }
  const category = categories?.find((item) => item.id === listing.providerCategoryId);
  return {
    location,
    states: listing.stateCodes.map(
      (code) => STATES.find((state) => state.value === code) ?? { value: code, label: code }
    ),
    providerCategory: listing.providerCategoryId
      ? {
          value: listing.providerCategoryId,
          label: category?.providerCategoryCode ?? `Category ${listing.providerCategoryId}`,
        }
      : undefined,
    aoiSpecialty: listing.aoiSpecialtyId
      ? { value: String(listing.aoiSpecialtyId), label: listing.specialtyName ?? '' }
      : undefined,
    subspecialties: subspecialtiesToOptions(listing.subspecialties),
    compareListing: { value: listing.id, label: listing.listingTitle },
  };
};

// Fields the compare listing fills; "Edited from ..." appears once any of them differs.
export const PREFILLED_KEYS = [
  'location',
  'states',
  'providerCategory',
  'aoiSpecialty',
  'subspecialties',
] as const;

const signature = (values: Partial<OfferingSearchValues>) =>
  JSON.stringify(
    PREFILLED_KEYS.map((key) => {
      const value = values[key];
      if (Array.isArray(value)) return value.map((item) => item.value).sort();
      return value?.value ?? null;
    })
  );

export const isEditedFromListing = (
  current: Partial<OfferingSearchValues>,
  prefilled: Partial<OfferingSearchValues> | undefined
) => prefilled != null && signature(current) !== signature(prefilled);

// The buyer's listing a result's fit was scored against.
export const compareListingFor = (
  fitCompareListingId: number | null | undefined,
  compareListings: CompareListingOption[]
) =>
  fitCompareListingId == null
    ? undefined
    : compareListings.find((item) => item.id === fitCompareListingId);
